import { useState } from 'react';
import { DateTime } from 'luxon';
import { Box, Typography, Stack, Chip, Button, Divider } from '@mui/material';
import { CloudOutlined } from '@mui/icons-material';
import CredentialDialog from '../Dialogs/CredentialsDialog';
import { CREDENTIAL_PROVIDERS } from '../../Pages/Homepage/Helper';



function IntegrationRow({ integration, onEnable, onManage }) {
  const isActive   = integration.active_integration > 0;
  const updatedAt  = DateTime.fromISO(integration.updated_at).toFormat('MMM d, yyyy');
  const needsCreds = Boolean(CREDENTIAL_PROVIDERS[integration.provider]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleEnable = () => {
    if (needsCreds) {
      setDialogOpen(true);  // open credential dialog
    } else {
      onEnable?.(integration); // standard OAuth / redirect flow
    }
  };

  const handleCredentialSubmit = (credentials) => {
    onEnable?.(integration, credentials);
    setDialogOpen(false);
  };

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.75,
        px: 2, py: 1.75,
        borderBottom: '0.5px solid', borderColor: 'divider',
        transition: 'background 0.1s',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { bgcolor: 'action.hover' },
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
          bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <CloudOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.4 }}>
            <Typography variant="body2" fontWeight={500} noWrap>{integration.provider}</Typography>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: isActive ? '#22c55e' : '#ef4444' }} />
            <Chip
              size="small"
              label={isActive ? 'Active' : 'Not connected'}
              sx={{
                fontSize: 11, height: 20, borderRadius: '20px', border: 'none',
                bgcolor: isActive ? 'success.50' : 'error.50',
                color:   isActive ? 'success.dark' : 'error.dark',
              }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block">{integration.description}</Typography>
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.4 }}>Last updated {updatedAt}</Typography>
        </Box>

        {isActive ? (
          <Button size="small" variant="outlined" color="info" onClick={() => setDialogOpen(true)} sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            Manage
          </Button>
        ) : (
          <Button size="small" variant="outlined" color="inherit" onClick={handleEnable} sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            Enable
          </Button>
        )}
      </Box>

      {/* Credential dialog — only mounts for providers that need it */}
      {needsCreds && (
        <CredentialDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleCredentialSubmit}
          provider={integration.provider}
        />
      )}
    </>
  );
}

export default function IntegrationsList({ integrations = null, onEnable, onManage }) {
  return (
    <Box sx={{ border: '0.5px solid', borderColor: 'divider', overflow: 'hidden' }}>

      <Box sx={{ px: 2, py: 1.5, borderBottom: '0.5px solid', borderColor: 'divider' }}>
        <Typography variant="body2" fontWeight={500}>Integrations</Typography>
        <Typography variant="caption" color="text.secondary">
          Connect third-party services to your workspace
        </Typography>
      </Box>

      {integrations === null ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No integrations available</Typography>
        </Box>
      ) : (
        integrations.map(integration => (
          <IntegrationRow
            key={integration.id}
            integration={integration}
            onEnable={onEnable}
            onManage={onManage}
          />
        ))
      )}

    </Box>
  );
}